import { call, delay, put, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { END, eventChannel } from 'redux-saga';
import { fork, take } from 'redux-saga/effects';

import { notify } from '@xcritical/notification';

import {
  chatActions,
  getChatState,
  getCurrentCallSelector,
  getCurrentChatSelector,
  RTCClient,
} from '../store';
import { CallState } from '../call/types';
import { initializeSounds, playSound } from '../call/sound';
import { CALL_ERRORS } from '../consts';

import {
  getRtcClient,
  getSocket,
  handleChatError,
  removeRtcClient,
} from './helpers';
import { getToken } from './utils';

type EventType =
  | ReturnType<typeof chatActions.onIceCandidate>
  | ReturnType<typeof chatActions.sendSdp>
  | ReturnType<typeof chatActions.connectionStateChange>;

function createRTCChannel(rtcClient: RTCClient) {
  // `eventChannel` takes a subscriber function
  // the subscriber function takes an `emit` argument to put messages onto the channel
  return eventChannel((emit) => {
    const sendSdp = (sdp) => emit(chatActions.sendSdp(sdp));
    const sendIceCandidate = (e) => emit(chatActions.onIceCandidate(e));
    const connectionStateChange = (e) =>
      emit(chatActions.connectionStateChange(e));
    const changeClientMute = (e) => emit(chatActions.changeClientMute(e));

    const close = () => emit(END);
    rtcClient.subscribe('send-sdp', sendSdp);
    rtcClient.subscribe('iceCandidate', sendIceCandidate);
    rtcClient.subscribe('connectionStateChange', connectionStateChange);
    rtcClient.subscribe('iceConnectionStateChange', connectionStateChange);
    rtcClient.subscribe('changeClientMute', changeClientMute);

    rtcClient.subscribe('close', close);

    const unsubscribe = () => {
      rtcClient.unsubscribe('send-sdp', sendSdp);
      rtcClient.unsubscribe('iceCandidate', sendIceCandidate);
      rtcClient.unsubscribe('changeClientMute', changeClientMute);
      rtcClient.unsubscribe('connectionStateChange', connectionStateChange);
      rtcClient.unsubscribe('iceConnectionStateChange', connectionStateChange);
      rtcClient.unsubscribe('close', close);
    };

    return unsubscribe;
  });
}

export function* handleCallStateChange({ payload }: PayloadAction<CallState>) {
  if (payload.state?._ === 'callStateDiscarded') {
    yield put(chatActions.endCall());
    playSound('busy');
    notify.error('Client is busy');
  } else if (payload.state?._ === 'callStateError') {
    yield put(chatActions.endCall());
    playSound('busy');
    const errorMessage = payload.state.error.message;

    if (!Object.values(CALL_ERRORS).some((error) => error === errorMessage)) {
      notify.error('Client is busy');
    }
  }
}

export function* handleStartCall({
  payload,
}: PayloadAction<{ clientId: number; phoneIndex: number; channelId: number }>) {
  try {
    initializeSounds();
    const token = yield* getToken(payload.clientId);
    playSound('ringing');

    if (token) {
      const socket = getSocket();

      if (token) {
        socket.emit('call:start', {
          token,
          phoneIndex: payload.phoneIndex,
          channelId: payload.channelId,
        });
      }

      /*
      const currentCall = yield* call(apiStartCall, token, payload.phoneIndex);

      if (currentCall.chatId) {
        const { chatId } = currentCall;
        yield put(chatActions.setActiveChatId(chatId));
        yield delay(200);
        yield put(chatActions.getChatMessages(chatId));
      }

      
      */
    }
  } catch (e) {
    yield put(chatActions.endCall());
    handleChatError(e);
  }
}

export function* handleSendSdp({
  payload,
}: PayloadAction<RTCSessionDescriptionInit>) {
  try {
    const chat = yield* select(getCurrentChatSelector);
    const token = yield* getToken(chat?.clientId);
    const socket = getSocket();

    if (token) {
      socket.emit('call:send_sdp', {
        token,
        phoneIndex: chat?.phoneIndex!,
        channelId: chat?.channelId!,
        sdp: payload.sdp,
      });

      yield call(handleUpdateCall, chat!.clientId);
    }
  } catch (e) {
    handleChatError(e);
  }
}

export function* handleOnIceCandidate({
  payload,
}: PayloadAction<RTCPeerConnectionIceEvent>) {
  try {
    const { candidate } = payload;

    const chat = yield* select(getCurrentChatSelector);
    const token = yield* getToken(chat?.clientId);
    const socket = getSocket();

    if (token) {
      if (candidate) {
        socket.emit('call:send_icecandidate', {
          token,
          phoneIndex: chat?.phoneIndex!,
          channelId: chat?.channelId!,
          candidate: candidate.candidate,
        });
      } else {
        console.error(
          'RTCClient[handleOnIceCandidate]: empty candidate',
          payload
        );
        // yield* handleDiscardCall();
      }
    }
  } catch (e) {
    handleChatError(e);
  }
}

export function* handleDiscardCall() {
  try {
    const activeCall = yield* select(getCurrentCallSelector);
    const token = yield* getToken(activeCall?.clientId);
    const socket = getSocket();

    if (token && activeCall) {
      socket.emit('call:end', {
        token,
        phoneIndex: activeCall.phoneIndex!,
        channelId: activeCall.channelId,
      });
    }
  } catch (e) {
    handleChatError(e);
  } finally {
    yield getRtcClient().onDiscardCall();
    yield put(chatActions.endCall());
  }
}

export function* handleChangeClientMute({ payload }: PayloadAction<boolean>) {
  try {
    yield put(chatActions.setIsClientMuted(payload));
  } catch (e) {
    handleChatError(e);
  }
}

export function* handleConnectionStateChange({
  payload,
}: PayloadAction<string>) {
  try {
    if (payload === 'disconnected' || payload === 'failed') {
      const currentCall = yield* select(getCurrentCallSelector);
      const token = yield* getToken(currentCall?.clientId);

      if (token && currentCall) {
        const socket = getSocket();
        playSound('end');
        socket.emit('call:end', {
          token,
          phoneIndex: currentCall.phoneIndex,
          channelId: currentCall.channelId,
        });
      }

      yield getRtcClient().onDiscardCall();
      yield put(chatActions.endCall());
    }

    if (payload === 'connected') {
      yield put(chatActions.setCallIsPending(false));
    }
  } catch (e) {
    handleChatError(e);
  }
}

export function* handleCallSignalingData({
  payload,
}: PayloadAction<{ '@type': string; [key: string]: any }>) {
  try {
    switch (payload['@type']) {
      case 'callStateReady': {
        yield fork(watchRTCClient);
        const state = yield* select(getCurrentCallSelector);
        yield getRtcClient().connect(state!);
        break;
      }
      case 'hungUpCall': {
        yield getRtcClient().onDiscardCall();

        if (payload.message) notify.warn(payload.message);

        yield put(chatActions.endCall());
        break;
      }
      case CALL_ERRORS.USER_PRIVACY_RESTRICTED: {
        notify.warn(
          'You cannot call to user because of their privacy settings'
        );
        break;
      }
      case CALL_ERRORS.CALL_PROTOCOL_FLAGS_INVALID: {
        notify.warn('Call protocol flags invalid');
        break;
      }
      case CALL_ERRORS.USER_IS_BLOCKED: {
        notify.warn('You were blocked by this user');
        break;
      }
      case CALL_ERRORS.INPUT_USER_DEACTIVATED: {
        notify.warn('Current user was deleted');
        break;
      }
      case CALL_ERRORS.PARTICIPANT_VERSION_OUTDATED: {
        notify.warn(
          'Current user does not use an up to date telegram client with support for calls'
        );
        break;
      }
      case CALL_ERRORS.USER_ID_INVALID: {
        notify.warn('The provided telegram user ID is invalid');
        break;
      }
      default: {
        yield getRtcClient().p2pApplyCallSignalingData(payload);
      }
    }
  } catch (e) {
    handleChatError(e);
  }
}

export function* handleUpdateCall(leadId: number) {
  try {
    yield delay(500);
    const { callInProgress } = getRtcClient();

    if (!callInProgress) {
      yield put(chatActions.endCall());
      yield put(chatActions.microphoneToggle(true));
      removeRtcClient();
    }
  } catch (e) {
    handleChatError(e);
  } finally {
    const { activeCall } = yield* select(getChatState);

    if (activeCall) yield call(handleUpdateCall, leadId);
  }
}

export function* watchRTCClient() {
  const rtcClient = yield call(getRtcClient);
  const rtcChannel = yield* call(createRTCChannel, rtcClient);

  while (true) {
    try {
      // An error from socketChannel will cause the saga jump to the catch block
      const action: EventType = yield take(rtcChannel);
      yield put(action);
    } catch (err) {
      console.error('rtcClient error:', err);
    }
  }
}
